// LiveBlocks Collaboration
import {
  ClientSideSuspense,
  LiveblocksProvider,
  RoomProvider,
} from '@liveblocks/react/suspense';
import {Box, Stack} from '@mui/material';
import _ from 'lodash';
import React, {useCallback, useEffect} from 'react';
import {useSelector} from 'react-redux';
import {Outlet, useParams} from 'react-router-dom';

import {AppBarV2} from '../components/AppBar';
import {EmptyDocument} from '../components/report-workspace/EmptyDocumentLoading';
import SharedDocumentValidator from '../components/SharedDocumentValidator';
import {DocumentStyleProvider} from '../context/DocumentStyleProvider';
import {DrawerProvider} from '../context/DrawerContext';
import {NavigationGuardProvider} from '../context/NavigationGuardContext';
import {selectEffectiveOrg} from '../data/session';
import {
  authEndpoint,
  resolveMentionSuggestions,
  resolveRoomsInfo,
  resolveUsers,
  throttle,
} from '../liveblocks.config';
import {initCollabSocket} from '../util/collabSocketManager';

const WorkspaceLayout = () => {
  const {documentId} = useParams();

  const {isExternalUser, isShareLinkMode, orgConfig} = useSelector(
    (state) => state.session
  );
  const effectiveOrg = useSelector(selectEffectiveOrg);
  const orgId = effectiveOrg?.orgId;

  useEffect(() => {
    if (!orgConfig || Object.keys(orgConfig).length === 0) return;
    initCollabSocket(orgConfig?.collabBaseUrl);
  }, [orgConfig]);

  const _authEndpoint = useCallback(
    (passThruArgs) => authEndpoint(passThruArgs),
    []
  );
  const _resolveMentionSuggestions = useCallback(
    (passThruArgs) => resolveMentionSuggestions(passThruArgs, documentId),
    [documentId]
  );
  const _resolveRoomsInfo = useCallback(resolveRoomsInfo, []);
  const _resolveUsers = useCallback(
    (passThruArgs) => resolveUsers(passThruArgs, documentId),
    [documentId]
  );

  // Show loading state while orgConfig or orgId (which is derived) is not ready
  if (
    !orgId ||
    (((isExternalUser && !location.search.includes('shared=true')) ||
      !isShareLinkMode) &&
      (!orgConfig || Object.keys(orgConfig).length === 0))
  ) {
    return <EmptyDocument loading={true} />;
  }

  return (
    <LiveblocksProvider
      authEndpoint={_authEndpoint}
      preventUnsavedChanges
      resolveMentionSuggestions={_resolveMentionSuggestions}
      resolveRoomsInfo={_resolveRoomsInfo}
      resolveUsers={_resolveUsers}
      throttle={throttle}
    >
      <NavigationGuardProvider>
        <Stack direction={'column'} height={'100vh'} width={'100vw'}>
          <AppBarV2 />

          <DrawerProvider>
            <RoomProvider
              id={`${orgId}:${_.isNil(documentId) ? 'room' : documentId}`}
              initialPresence={{}}
            >
              <DocumentStyleProvider>
                <ClientSideSuspense fallback={<EmptyDocument loading={true} />}>
                  <Box display="flex">
                    <Box sx={{flexGrow: 1, overflow: 'auto'}}>
                      <Outlet />
                    </Box>
                  </Box>
                </ClientSideSuspense>
              </DocumentStyleProvider>
            </RoomProvider>
          </DrawerProvider>
        </Stack>
      </NavigationGuardProvider>
    </LiveblocksProvider>
  );
};

export const WorkspaceLayoutWrapper = () => {
  return (
    <SharedDocumentValidator fallback={<EmptyDocument loading={true} />}>
      <WorkspaceLayout />
    </SharedDocumentValidator>
  );
};

export default WorkspaceLayoutWrapper;
